import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDocumentData } from "react-firebase-hooks/firestore";
import {
  getDocs,
  query,
  orderBy,
  where,
  limit,
  QueryDocumentSnapshot,
} from "firebase/firestore";
import { format } from "date-fns";

import { useApi } from "~/hooks/use-api";
import { logError } from "~/shared/services/error-reporting";
import { toast } from "~/utils/toast";
import { downloadFromOrgFiles } from "~/integrations/firebase/storage";
import {
  getOnixImportReportDocument,
  getOnixImportReportsCollection,
  OnixImportReportExport,
} from "~/integrations/firebase/collections/onix-import-reports";

interface UseOnixImportReportExportArgs {
  reportId: string;
  open: boolean;
}

export function useOnixImportReportExport({
  reportId,
  open,
}: UseOnixImportReportExportArgs) {
  const api = useApi();
  const { t } = useTranslation("settings");

  const [exportId, setExportId] = useState<string | null>(null);
  const [isFetchingExistingExport, setIsFetchingExistingExport] =
    useState(false);
  const [isScheduling, setIsScheduling] = useState(false);

  const [exportDoc, isSubscribing] = useDocumentData<OnixImportReportExport>(
    exportId ? getOnixImportReportDocument(exportId) : null,
  );

  const reset = useCallback(() => {
    setExportId(null);
    setIsFetchingExistingExport(false);
    setIsScheduling(false);
  }, []);

  const findExistingExport =
    useCallback(async (): Promise<QueryDocumentSnapshot | null> => {
      try {
        const q = query(
          getOnixImportReportsCollection(),
          where("onixImportReportId", "==", reportId),
          orderBy("createdAt", "desc"),
          limit(1),
        );
        const snap = await getDocs(q);
        if (snap.empty) {
          return null;
        }
        return snap.docs[0];
      } catch (e) {
        logError(e as any);
        return null;
      }
    }, [reportId]);

  useEffect(() => {
    if (!open || exportId) return;

    const checkForExisting = async () => {
      setIsFetchingExistingExport(true);
      try {
        const existingDoc = await findExistingExport();
        if (existingDoc) {
          setExportId(existingDoc.id);
        }
      } finally {
        setIsFetchingExistingExport(false);
      }
    };

    checkForExisting().catch(() => {
      // This is intentionally ignored as we don't want a floating promise
    });
  }, [open, exportId, findExistingExport]);

  const schedule = useCallback(async () => {
    if (exportDoc && exportDoc.status !== "failed") {
      return;
    }

    // If we don't have a subscribed document, we need to check manually before scheduling
    if (!exportDoc) {
      const existingDoc = await findExistingExport();
      if (existingDoc) {
        const data = existingDoc.data() as OnixImportReportExport;
        if (data.status !== "failed") {
          // An export exists and is not failed, so we should just subscribe to it.
          setExportId(existingDoc.id);
          return;
        }
      }
    }

    setIsScheduling(true);
    try {
      const result = await api.reports.scheduleOnixImportReport(reportId);
      if (result?.exportId) {
        setExportId(result.exportId);
      }
    } catch (error: any) {
      logError(error);
      toast.error(error?.message);
    } finally {
      setIsScheduling(false);
    }
  }, [api.reports, reportId, exportDoc, findExistingExport]);

  const download = useCallback(async () => {
    if (!exportDoc?.storagePath) return;
    try {
      await downloadFromOrgFiles(
        exportDoc.storagePath,
        `onix-report-${format(new Date(), "yyyy-MM-dd")}.csv`,
      );
    } catch (e: any) {
      logError(e);
      toast.error(t("organisation.onix.misc.downloadError"));
    }
  }, [exportDoc, t]);

  const isDownloadReady = exportDoc?.status === "completed";
  const isGenerating = Boolean(
    isScheduling ||
      (exportId &&
        (!exportDoc ||
          (exportDoc.status !== "completed" && exportDoc.status !== "failed"))),
  );
  const isActionLoading =
    isFetchingExistingExport || isSubscribing || isGenerating;
  const showActionSkeleton =
    !exportId && (isFetchingExistingExport || isSubscribing);

  return {
    // ui derived
    isDownloadReady,
    isActionLoading,
    showActionSkeleton,
    // actions
    schedule,
    download,
    reset,
  };
}
